import { ArrowDown, ArrowUp, ArrowUpDown, ChevronLeft, ChevronRight } from 'lucide-react';
import * as React from 'react';
import { cn } from '../../lib/cn.js';
import { Button } from '../primitives/button.js';
import { Checkbox } from '../primitives/checkbox.js';
import { EmptyState, Skeleton } from '../primitives/states.js';

export type SortDirection = 'asc' | 'desc';
export interface SortState {
  columnId: string;
  direction: SortDirection;
}

export interface DataTableColumn<T> {
  id: string;
  header: React.ReactNode;
  cell: (row: T) => React.ReactNode;
  /** Server-side sortable: clicking calls onSortChange; the table never sorts locally. */
  sortable?: boolean;
  align?: 'left' | 'right' | 'center';
  /** Tailwind width/min-width classes for the column, e.g. "min-w-48". */
  className?: string;
  /** Numeric columns use tabular numerals and right alignment by default. */
  numeric?: boolean;
  /** Keep the column visible while horizontally scrolling. */
  sticky?: boolean;
}

export interface DataTableSelection {
  selectedIds: ReadonlySet<string>;
  onChange: (next: Set<string>) => void;
}

export interface DataTableProps<T> {
  columns: readonly DataTableColumn<T>[];
  rows: readonly T[];
  getRowId: (row: T) => string;
  /** Accessible table caption (visually hidden unless showCaption). */
  caption: string;
  showCaption?: boolean;
  sort?: SortState | null;
  onSortChange?: (sort: SortState) => void;
  loading?: boolean;
  /** Rendered when not loading and rows is empty. */
  empty?: React.ReactNode;
  selection?: DataTableSelection;
  onRowClick?: (row: T) => void;
  /** Accessible label for the row action when onRowClick is set. */
  rowActionLabel?: (row: T) => string;
  /** Pagination (or any footer) slot. */
  footer?: React.ReactNode;
  /** Toolbar slot rendered above the table (e.g. bulk actions for the selection). */
  toolbar?: React.ReactNode;
  className?: string;
  density?: 'comfortable' | 'compact';
}

export function DataTable<T>({
  columns,
  rows,
  getRowId,
  caption,
  showCaption,
  sort,
  onSortChange,
  loading,
  empty,
  selection,
  onRowClick,
  rowActionLabel,
  footer,
  toolbar,
  className,
  density = 'comfortable',
}: DataTableProps<T>) {
  const cellPad = density === 'compact' ? 'px-3 py-2' : 'px-4 py-3';
  const allIds = rows.map(getRowId);
  const selectedCount = selection ? allIds.filter((id) => selection.selectedIds.has(id)).length : 0;
  const headerChecked = selectedCount === 0 ? false : selectedCount === allIds.length ? true : 'indeterminate';
  const colCount = columns.length + (selection ? 1 : 0);

  const toggleSort = (col: DataTableColumn<T>) => {
    if (!onSortChange) return;
    const direction: SortDirection = sort?.columnId === col.id && sort.direction === 'desc' ? 'asc' : 'desc';
    onSortChange({ columnId: col.id, direction });
  };

  return (
    <div className={cn('min-w-0', className)}>
      {toolbar ? <div className="mb-3">{toolbar}</div> : null}
      <div className="relative overflow-x-auto rounded-menu border border-border-subtle" tabIndex={0} role="region" aria-label={caption}>
        <table className="w-full border-collapse text-left text-body" aria-busy={loading || undefined}>
          <caption className={showCaption ? 'px-4 py-2 text-left text-helper text-text-secondary' : 'sr-only'}>{caption}</caption>
          <thead className="bg-surface-subtle">
            <tr>
              {selection ? (
                <th scope="col" className={cn(cellPad, 'w-12')}>
                  <Checkbox
                    aria-label="Seleccionar todas las filas"
                    checked={headerChecked}
                    onCheckedChange={(c) => selection.onChange(c === true ? new Set(allIds) : new Set())}
                  />
                </th>
              ) : null}
              {columns.map((col) => {
                const active = sort?.columnId === col.id;
                const ariaSort = active ? (sort.direction === 'asc' ? 'ascending' : 'descending') : col.sortable ? 'none' : undefined;
                const align = col.align ?? (col.numeric ? 'right' : 'left');
                return (
                  <th
                    key={col.id}
                    scope="col"
                    aria-sort={ariaSort}
                    className={cn(
                      cellPad,
                      'whitespace-nowrap border-b border-border-subtle text-helper font-semibold text-text-secondary',
                      align === 'right' && 'text-right',
                      align === 'center' && 'text-center',
                      col.sticky && 'sticky left-0 z-[1] bg-surface-subtle',
                      col.className,
                    )}
                  >
                    {col.sortable && onSortChange ? (
                      <button
                        type="button"
                        onClick={() => toggleSort(col)}
                        className={cn(
                          'inline-flex items-center gap-1 rounded-[6px] hover:text-text-primary focus-visible:outline-2 focus-visible:outline-focus',
                          active && 'text-text-primary',
                          align === 'right' && 'flex-row-reverse',
                        )}
                      >
                        {col.header}
                        {active ? (
                          sort.direction === 'asc' ? (
                            <ArrowUp aria-hidden className="size-3.5" />
                          ) : (
                            <ArrowDown aria-hidden className="size-3.5" />
                          )
                        ) : (
                          <ArrowUpDown aria-hidden className="size-3.5 opacity-60" />
                        )}
                      </button>
                    ) : (
                      col.header
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0
              ? Array.from({ length: 5 }, (_, i) => (
                  <tr key={`sk-${i}`} className="border-b border-border-subtle last:border-b-0">
                    {Array.from({ length: colCount }, (__, j) => (
                      <td key={j} className={cellPad}>
                        <Skeleton className="h-4 w-full max-w-40" />
                      </td>
                    ))}
                  </tr>
                ))
              : null}
            {!loading && rows.length === 0 ? (
              <tr>
                <td colSpan={colCount}>{empty ?? <EmptyState compact title="Sin resultados" description="Ajusta los filtros para ver datos." />}</td>
              </tr>
            ) : null}
            {rows.map((row) => {
              const id = getRowId(row);
              const selected = selection?.selectedIds.has(id) ?? false;
              return (
                <tr
                  key={id}
                  aria-selected={selection ? selected : undefined}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  className={cn(
                    'border-b border-border-subtle bg-surface-default last:border-b-0',
                    onRowClick && 'cursor-pointer hover:bg-surface-subtle',
                    selected && 'bg-brand-tint hover:bg-brand-tint',
                    loading && 'opacity-60',
                  )}
                >
                  {selection ? (
                    <td className={cellPad} onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        aria-label="Seleccionar fila"
                        checked={selected}
                        onCheckedChange={(c) => {
                          const next = new Set(selection.selectedIds);
                          if (c === true) next.add(id);
                          else next.delete(id);
                          selection.onChange(next);
                        }}
                      />
                    </td>
                  ) : null}
                  {columns.map((col, ci) => {
                    const align = col.align ?? (col.numeric ? 'right' : 'left');
                    const content = col.cell(row);
                    return (
                      <td
                        key={col.id}
                        className={cn(
                          cellPad,
                          'align-middle text-text-primary',
                          col.numeric && 'cm-tabular',
                          align === 'right' && 'text-right',
                          align === 'center' && 'text-center',
                          col.sticky && cn('sticky left-0 z-[1]', selected ? 'bg-brand-tint' : 'bg-surface-default'),
                          col.className,
                        )}
                      >
                        {ci === 0 && onRowClick ? (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              onRowClick(row);
                            }}
                            aria-label={rowActionLabel?.(row)}
                            className="max-w-full text-left font-medium text-text-primary hover:text-brand-primary hover:underline focus-visible:outline-2 focus-visible:outline-focus"
                          >
                            {content}
                          </button>
                        ) : (
                          content
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {footer ? <div className="mt-3">{footer}</div> : null}
    </div>
  );
}

export interface PaginationProps {
  /** e.g. "1–25 de 312" */
  summary?: React.ReactNode;
  hasPrevious: boolean;
  hasNext: boolean;
  onPrevious: () => void;
  onNext: () => void;
  loading?: boolean;
  className?: string;
}

/** Cursor-friendly pagination (previous / next). */
export function Pagination({ summary, hasPrevious, hasNext, onPrevious, onNext, loading, className }: PaginationProps) {
  return (
    <nav aria-label="Paginación" className={cn('flex flex-wrap items-center justify-between gap-3', className)}>
      <p className="cm-tabular text-helper text-text-secondary" aria-live="polite">
        {summary}
      </p>
      <div className="flex items-center gap-2">
        <Button variant="secondary" size="sm" onClick={onPrevious} disabled={!hasPrevious || loading} leadingIcon={<ChevronLeft aria-hidden />}>
          Anterior
        </Button>
        <Button variant="secondary" size="sm" onClick={onNext} disabled={!hasNext || loading}>
          Siguiente
          <ChevronRight aria-hidden />
        </Button>
      </div>
    </nav>
  );
}
